import React, { useState, useEffect } from 'react';
import { colors, API } from '../utils/theme';
import { PageHeader, Button, Badge } from '../components/UI';

export default function ImportacaoPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [xmilesStatus, setXmilesStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/xmiles/status`).then(r => r.json()).then(setXmilesStatus).catch(() => {});
  }, []);

  const importar = async (tipo, file) => {
    if (!file) return;
    setLoading(true);
    setStatus(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/importar/${tipo}`, { method: 'POST', body: formData });
      const data = await res.json();
      setStatus({ tipo, ...data, success: !data.error });
    } catch (e) {
      setStatus({ tipo, success: false, error: e.message });
    }
    setLoading(false);
  };

  const syncXmiles = async (syncType = 'all') => {
    setSyncing(true);
    setSyncResult(null);
    try {
      if (!xmilesStatus?.authenticated) {
        await fetch(`${API}/xmiles/auth`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      }
      const res = await fetch(`${API}/xmiles/sync`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ syncType }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSyncResult({ success: true, ...data.results });
      fetch(`${API}/xmiles/status`).then(r => r.json()).then(setXmilesStatus).catch(() => {});
    } catch (e) {
      setSyncResult({ success: false, error: e.message });
    }
    setSyncing(false);
  };

  return (
    <div>
      <PageHeader title="Importação / Sincronização" subtitle="Importar dados de planilhas ou sincronizar com XMiles Manager" />

      {status && (
        <div style={{
          background: status.success ? `${colors.success}11` : `${colors.danger}11`,
          border: `1px solid ${status.success ? colors.success : colors.danger}44`,
          borderRadius: 12, padding: 16, marginBottom: 20,
        }}>
          {status.success ? (
            <div>
              <strong style={{ color: colors.success }}>Importação concluída!</strong>
              <div style={{ marginTop: 8 }}>Importados: {status.imported} de {status.total}</div>
              {status.errors?.length > 0 && <div style={{ color: colors.warning, marginTop: 4 }}>Erros: {status.errors.length}</div>}
            </div>
          ) : (
            <div style={{ color: colors.danger }}>Erro: {status.error}</div>
          )}
        </div>
      )}

      {/* XMiles Manager Sync */}
      <div style={{
        background: colors.card, borderRadius: 12, padding: 24, marginBottom: 24,
        border: `1px solid ${colors.primary}44`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Sincronização XMiles Manager</div>
            <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
              Importa automaticamente lançamentos, cartões e titulares da API do XMiles Manager.
            </div>
          </div>
          <Badge
            text={xmilesStatus?.authenticated ? 'Conectado' : 'Desconectado'}
            color={xmilesStatus?.authenticated ? colors.success : colors.textDim}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button onClick={() => syncXmiles('all')} disabled={syncing}>
            {syncing ? 'Sincronizando...' : 'Sincronizar Tudo'}
          </Button>
          <Button variant="outline" color={colors.border} onClick={() => syncXmiles('transactions')} disabled={syncing}>
            Só Lançamentos
          </Button>
          <Button variant="outline" color={colors.border} onClick={() => syncXmiles('cards')} disabled={syncing}>
            Só Cartões
          </Button>
          <Button variant="outline" color={colors.border} onClick={() => syncXmiles('holders')} disabled={syncing}>
            Só Titulares
          </Button>
        </div>

        {syncResult && (
          <div style={{
            marginTop: 16, padding: 12, borderRadius: 8,
            background: syncResult.success ? `${colors.success}11` : `${colors.danger}11`,
            border: `1px solid ${syncResult.success ? colors.success : colors.danger}44`,
          }}>
            {syncResult.success ? (
              <div>
                <strong style={{ color: colors.success }}>Sincronização concluída!</strong>
                {syncResult.holders && (
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    <strong>Titulares:</strong> {syncResult.holders.imported} novos, {syncResult.holders.updated} existentes
                  </div>
                )}
                {syncResult.cards && (
                  <div style={{ marginTop: 4, fontSize: 13 }}>
                    <strong>Cartões:</strong> {syncResult.cards.imported} novos, {syncResult.cards.updated} atualizados
                  </div>
                )}
                {syncResult.transactions && (
                  <div style={{ marginTop: 4, fontSize: 13 }}>
                    <strong>Lançamentos:</strong> {syncResult.transactions.imported} importados, {syncResult.transactions.skipped} já existiam
                    {syncResult.transactions.errors > 0 && <span style={{ color: colors.warning }}> ({syncResult.transactions.errors} erros)</span>}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: colors.danger }}>Erro: {syncResult.error}</div>
            )}
          </div>
        )}
      </div>

      {/* File Import */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <ImportCard title="Cadastro de Cartões"
          desc="Importa a planilha de cadastro com nome, banco, limite, bandeira, status de cada cartão."
          tipo="cadastro-cartoes" onImport={importar} loading={loading} accept=".xlsx,.xls,.csv" />
        <ImportCard title="Controle de Faturas"
          desc="Importa os lançamentos das faturas com valores, vencimentos e status por titular."
          tipo="faturas" onImport={importar} loading={loading} accept=".xlsx,.xls,.csv" />
      </div>
    </div>
  );
}

function ImportCard({ title, desc, tipo, onImport, loading, accept }) {
  return (
    <div style={{
      background: colors.card, borderRadius: 12, padding: 24,
      border: `1px solid ${colors.border}`,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>{desc}</div>
      <label style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '10px 20px', borderRadius: 8, background: colors.primary, color: '#fff',
        fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.5 : 1,
      }}>
        {loading ? 'Importando...' : 'Selecionar Arquivo'}
        <input type="file" accept={accept} style={{ display: 'none' }}
          onChange={e => onImport(tipo, e.target.files[0])} disabled={loading} />
      </label>
    </div>
  );
}
